import { useRef, useState } from 'react'
import { catalogApi } from '../lib/api'
import { imageUrl, MENU_GROUPS, MENU_IMAGES } from '../lib/menuImages'
import { useApp } from '../lib/store'
import { Sheet } from './Sheet'

/**
 * Chọn ảnh cho món.
 *
 * Đường nhanh là bấm một tấm trong kho dựng sẵn — chủ quán chỉ còn phải gõ giá. Món hiếm
 * không có trong kho thì tự chụp một tấm; máy chủ thu nhỏ và cắt vuông rồi mới cất.
 *
 * Bấm vào ảnh trong kho là <b>điền luôn cả tên và đơn vị</b> nếu ô tên còn trống. Người ta
 * chọn tấm "Phở bò" thì món đó tên là Phở bò, tính bằng tô — hỏi lại là thừa.
 */

interface Props {
  value: string | null
  /** `name` và `unit` chỉ gợi ý; nơi gọi tự quyết có nhận hay không. */
  onPick: (key: string | null, suggest?: { name: string; unit: string }) => void
  onClose: () => void
}

export function ImagePicker({ value, onPick, onClose }: Props) {
  const say = useApp((s) => s.say)
  const file = useRef<HTMLInputElement>(null)

  const [group, setGroup] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const shown = group ? MENU_IMAGES.filter((m) => m.group === group) : MENU_IMAGES

  async function upload(f: File) {
    if (!f.type.startsWith('image/')) {
      say('Chọn một tấm ảnh')
      return
    }

    setBusy(true)
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader()
        r.onload = () => resolve(String(r.result))
        r.onerror = () => reject(new Error('Không đọc được ảnh'))
        r.readAsDataURL(f)
      })

      const { key } = await catalogApi.uploadProductImage(base64)
      onPick(key)
      say('Đã tải ảnh lên')
      onClose()
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa tải được ảnh')
    }
    setBusy(false)
  }

  return (
    <Sheet title="Chọn ảnh món" onClose={onClose}>
      <p className="hint">
        Bấm một tấm có sẵn là xong — tên và đơn vị điền luôn, bạn chỉ cần gõ giá. Món lạ thì
        chụp ảnh riêng.
      </p>

      <div className="chip-row">
        <button type="button" className="chip" aria-pressed={group === null} onClick={() => setGroup(null)}>
          Tất cả
        </button>
        {MENU_GROUPS.map((g) => (
          <button
            key={g}
            type="button"
            className="chip"
            aria-pressed={group === g}
            onClick={() => setGroup(g)}
          >
            {g}
          </button>
        ))}
      </div>

      <div className="pick-grid">
        {shown.map((m) => (
          <button
            key={m.key}
            type="button"
            className={`pick-tile${value === m.key ? ' is-on' : ''}`}
            onClick={() => {
              onPick(m.key, { name: m.name, unit: m.unit })
              onClose()
            }}
          >
            <img src={imageUrl(m.key) ?? ''} alt="" loading="lazy" />
            <span>{m.name}</span>
          </button>
        ))}
      </div>

      <input
        ref={file}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) void upload(f)
        }}
      />

      <div className="sheet-actions">
        {value && (
          <button
            type="button"
            className="btn btn-danger-ghost"
            onClick={() => {
              onPick(null)
              onClose()
            }}
          >
            Bỏ ảnh
          </button>
        )}
        <button
          type="button"
          className="btn btn-ghost grow"
          disabled={busy}
          onClick={() => file.current?.click()}
        >
          {busy ? 'Đang tải…' : '📷 Ảnh của quán'}
        </button>
      </div>
    </Sheet>
  )
}
